import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedPatient } from '@store/reducers/patientmanagement.reducer'
import { requestPatientSelection } from '@store/reducers/uiSlice.reducer'
import { Container, Row, Col } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import ClaimFeature from '@components/pages/Claims/ClaimFeature/ClaimFeature.component'
import AddClaimsModal from '@components/layout/Modals/AddClaims/AddClaimsModal.component'
import InsurancePaymentModal from '@components/layout/Modals/InsurancePaymentModal/InsurancePaymentModal.component'
import ProcedureSettingsModal from '@components/layout/Modals/ProcedureSettingsModal/ProcedureSettingsModal.component'
import { fetchAllFeatureClaims } from '@store/actions/claimFeature.action'
import { RootState, AppDispatch } from '@store/index'
import { Claim } from '@dataTypes/claimFeature.types'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import TrackingManagement from '@components/pages/Claims/TrackingFeature/TrackingManagement.component'
import { FilterSelection as TrackingFilterSelection } from '@components/pages/Claims/TrackingFeature/TrackingFilterDropdown'
import AddClaimTracking from '@components/layout/Modals/AddClaimTracking/AddClaimTracking.component'
import GoBackButton from '@components/common/GoBackButton/GoBackButton.component'
import { fetchClaims } from '@store/actions/claims.action'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import { deleteClaimById } from '@services/api/claims.api'
import { filteredClaimsData, filteredTrackingData } from '@utils/claims.util'
import { useAppSelector } from '@store/hooks'
import SearchWithFilter, {
  FilterSelection,
} from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import ClaimsSimulationEditor from './ClaimsSimulationEditor'
import ClaimsSkeleton from './ClaimsSkeleton.page'
import { ConfirmationModal } from '@components/common'
import { EmptyState } from '@components/ui'
import { postAllTransactions } from '@services/api/tracking.api'
import {
  FIRST_LOAD_OVERLAY_MS,
  FIRST_LOAD_OVERLAY_STYLE,
  waitAtLeast,
} from '@utils/waitAtLeast.util'
import { logger } from '@utils/safeLogger'
const TAB_SKELETON_DURATION_MS = 500
const CLAIMS_LOADING_CONTAINER = '.claims-page'

const StableInput = React.memo(function StableInput({
  searchQuery,
  setSearchQuery,
  clearAllFilters,
  filter,
  handleFilterChange,
  placeholder,
}: {
  searchQuery: string
  setSearchQuery: (value: string) => void
  clearAllFilters: () => void
  filter: { data: any; labels: any }
  handleFilterChange: (newFilters: Partial<FilterSelection>) => void
  placeholder: string
}) {
  const inputElement = document.querySelector(
    '.search-claims-filter-container input.search-input',
  )
  if (inputElement) {
    ;(inputElement as HTMLInputElement).focus()
  }
  return (
    <div className="search-claims-filter-container">
      <SearchWithFilter
        searchIcon="mgc_search_2_line"
        filterIcon="mgc_settings_2_line"
        filterLabel=""
        placeholder={placeholder}
        searchQuery={searchQuery}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setSearchQuery(e.target.value)
        }}
        onFilterChange={(selectedFilters: FilterSelection) => {
          handleFilterChange(selectedFilters)
        }}
        onReset={clearAllFilters}
        filters={filter.data}
        filterLabels={filter.labels}
      />
    </div>
  )
})

function Claims(): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>()
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { data: claimsNew } = useSelector((state: RootState) => state.claims)
  const location = useLocation()
  const navigate = useNavigate()
  const { showAddClaim, tabKey, previousPage } = location.state || {}
  const [pagePreviousState, setPagePreviousState] = useState<any>(previousPage)
  // Claim id to auto-open the ViewClaimPostingInfo modal for — populated
  // from a notification deep-link (`location.state.{claimId, viewClaim}`).
  // Cleared by `handleInitialViewConsumed` once ClaimFeature has acted on it.
  const [initialViewClaimId, setInitialViewClaimId] = useState<string | null>(
    location.state?.viewClaim ? (location.state?.claimId ?? null) : null,
  )
  const [isAddClaimsModal, setIsAddClaimsModal] = useState<boolean>(
    showAddClaim || false,
  )
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState<boolean>(false)
  const [isProcedureSettingsModalOpen, setIsProcedureSettingsModalOpen] =
    useState<boolean>(false)
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [isTabSkeleton, setIsTabSkeleton] = useState(false)
  const [tabSkeletonKey, setTabSkeletonKey] = useState(0)
  const [activeTab, setActiveTab] = useState<
    'pre-auth' | 'tracking' | 'claims'
  >(tabKey || 'claims')
  const isPageReadyRef = useRef(false)
  const prevTabRef = useRef(activeTab)
  const [isPageReady, setIsPageReady] = useState(false)
  const [isAddTrackingModalVisible, setIsAddTrackingModalVisible] =
    useState(false)
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
  const [claimIdToDelete, setClaimIdToDelete] = useState<string>('')
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  // Patient list for resolving a notification deep-link's `patientId` to the
  // full Patient record needed by `setSelectedPatient`. The Claims page
  // filters `currentClaimsData` by `selectedPatient?.patient_id`, so the
  // patient must match before the deep-linked claim can appear in the table.
  const patientList = useAppSelector((state) => state.patient.data ?? [])
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const [showClaimsSimulationEditor, setShowClaimsSimulationEditor] =
    useState(false)
  const [showPostConfirmationModal, setShowPostConfirmationModal] =
    useState(false)

  const clearAllFilters = (): void => {
    setSearchQuery('')
  }

  const clinicId = selectedClinic?.clinic_id

  useLayoutEffect(() => {
    showLoading({
      container: CLAIMS_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
  }, [showLoading])

  useEffect(() => {
    let cancelled = false
    const startedAt = Date.now()
    isPageReadyRef.current = false
    setIsPageReady(false)
    showLoading({
      container: CLAIMS_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
    void Promise.all([
      dispatch(fetchAllFeatureClaims()),
      dispatch(fetchClaims()),
    ])
      .then(async () => {
        await waitAtLeast(startedAt, FIRST_LOAD_OVERLAY_MS)
      })
      .finally(() => {
        if (cancelled) return
        isPageReadyRef.current = true
        setIsPageReady(true)
        hideLoading(CLAIMS_LOADING_CONTAINER)
      })

    return () => {
      cancelled = true
    }
  }, [clinicId, dispatch, hideLoading, showLoading])

  useEffect(() => {
    return () => {
      hideLoading(CLAIMS_LOADING_CONTAINER)
    }
  }, [hideLoading])

  useEffect(() => {
    if (isPageReadyRef.current && prevTabRef.current !== activeTab) {
      setIsTabSkeleton(true)
      setTabSkeletonKey((key) => key + 1)
    }
    prevTabRef.current = activeTab
  }, [activeTab])

  useEffect(() => {
    if (!isTabSkeleton) return
    const timer = setTimeout(() => {
      setIsTabSkeleton(false)
    }, TAB_SKELETON_DURATION_MS)
    return () => clearTimeout(timer)
  }, [isTabSkeleton, tabSkeletonKey])

  useEffect(() => {
    const element = document.querySelector('.page-content')

    if (element) {
      element.classList.add('claims-main-page')
    }

    return () => {
      if (element) {
        element.classList.remove('claims-main-page')
      }
    }
  }, [])

  const refreshClaims = useCallback(() => {
    dispatch(fetchClaims())
  }, [dispatch])

  const handleChangeTab = useCallback(
    (tab: string) => {
      if (
        (tab === 'pre-auth' || tab === 'tracking' || tab === 'claims') &&
        tab !== activeTab
      ) {
        setActiveTab(tab)
      }
    },
    [activeTab],
  )

  const [filters, setFilters] = useState<TrackingFilterSelection>({
    modeOfPayment: [],
    insuranceCarrier: [],
    checkStatus: [],
  })

  useEffect(() => {
    setSearchQuery('')
    setFilters({ modeOfPayment: [], insuranceCarrier: [], checkStatus: [] })
  }, [activeTab])

  // Notification deep-link bookkeeping. `pendingDeepLinkPatientIdRef` survives
  // the navigate-replace below so the deferred patient-list effect can still
  // apply the patient selection if the list hadn't loaded yet on first run.
  const pendingDeepLinkPatientIdRef = useRef<string | null>(null)

  // Single owner of /claims notification deep-link consumption. Narrow dep
  // (`location.state` only) by design: re-running this on every `activeTab`
  // or `selectedPatient` change would clobber subsequent manual user toggles
  // with the stale deep-link payload. We stash the patient id into a ref,
  // immediately clear `location.state` via navigate-replace (so back/forward
  // and remounts don't replay), and let a separate effect apply the patient
  // selection once `patientList` is non-empty.
  useEffect(() => {
    const state = location.state as
      | {
          tabKey?: 'claims' | 'pre-auth' | 'tracking'
          patientId?: string
          claimId?: string
          viewClaim?: boolean
          previousPage?: unknown
        }
      | null
      | undefined
    if (!state || !state.viewClaim) return

    if (
      state.tabKey === 'claims' ||
      state.tabKey === 'pre-auth' ||
      state.tabKey === 'tracking'
    ) {
      setActiveTab(state.tabKey)
    }
    if (state.patientId) {
      pendingDeepLinkPatientIdRef.current = state.patientId
    }
    if (state.claimId) {
      setInitialViewClaimId(state.claimId)
    }

    // Drop the consumed deep-link from history so a remount/back-forward
    // can't replay the open. Preserve `previousPage` if present (legacy
    // GoBackButton wiring).
    navigate(location.pathname, {
      replace: true,
      state: state.previousPage ? { previousPage: state.previousPage } : null,
    })
  }, [location.state, location.pathname, navigate])

  // Apply a pending deep-linked patient selection once the patient list is
  // available. Idempotent: clears the ref after one of (a) the patient was
  // dispatched, (b) the patient already matches, or (c) the patient list
  // loaded and the id wasn't in it (so we don't keep retrying forever).
  useEffect(() => {
    const pid = pendingDeepLinkPatientIdRef.current
    if (!pid) return
    if (pid === selectedPatient?.patient_id) {
      pendingDeepLinkPatientIdRef.current = null
      return
    }
    if (patientList.length === 0) return
    const target = patientList.find((p) => p.patient_id === pid)
    if (target) {
      dispatch(setSelectedPatient({ patient: target }))
    }
    pendingDeepLinkPatientIdRef.current = null
  }, [patientList, selectedPatient?.patient_id, dispatch])

  const handleInitialViewConsumed = useCallback((): void => {
    setInitialViewClaimId(null)
  }, [])

  const claimsSourceData = useMemo(
    () =>
      filteredClaimsData(claimsNew, activeTab, '', selectedPatient?.patient_id),
    [claimsNew, activeTab, selectedPatient?.patient_id],
  )

  const currentClaimsData = useMemo(
    () =>
      filteredClaimsData(
        claimsNew,
        activeTab,
        searchQuery,
        selectedPatient?.patient_id,
      ),
    [claimsNew, activeTab, searchQuery, selectedPatient?.patient_id],
  )

  const currentTrackingData = useMemo(
    () => filteredTrackingData(claimsNew),
    [claimsNew],
  )

  const hasNoClaimRecords = claimsSourceData.length === 0
  const canAddClaim = Boolean(selectedPatient?.patient_id)
  const showClaimEmptyCta = hasNoClaimRecords && canAddClaim
  const showClaimsListEmpty = currentClaimsData.length === 0
  const showTrackingEmpty = currentTrackingData.length === 0

  const openAddClaimModal = (): void => {
    setSelectedClaim(null)
    setIsAddClaimsModal(true)
  }

  const openAddTrackingModal = (): void => {
    setIsAddTrackingModalVisible(true)
  }

  const handleEditClaim = (claim: Claim): void => {
    const updatedClaim =
      currentClaimsData.find((c) => c.id === claim.id) || claim
    setSelectedClaim(updatedClaim)

    if (activeTab === 'tracking') {
      setIsPaymentModalOpen(true)
    } else {
      setIsAddClaimsModal(true)
    }
  }

  const tabButtons = [
    { label: 'Claims', value: 'claims' },
    { label: 'Pre-Auth', value: 'pre-auth' },
    { label: 'Tracking', value: 'tracking' },
  ]

  const handleFilterChange = (newFilters: Partial<FilterSelection>): void => {
    setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }))
  }

  const getUniqueInsuranceCarriers = (): string[] => {
    let data = []

    if (claimsNew && 'claims' in claimsNew) {
      data = claimsNew.claims as unknown as any[]
    }

    if (data.length > 0) {
      return Array.from(
        new Set(
          data
            .filter((claim: any) => {
              if (activeTab === 'pre-auth')
                return claim.claim_type === 'Pre-Authorization'
              if (activeTab === 'claims') return claim.claim_type === 'Claims'
              return true
            })
            .map((claim: any) => claim.insuranceplan.carrier.carrier_name),
        ),
      )
    }

    return []
  }

  const getUniqueInsuranceCarriersTracking = (): string[] => {
    let data = []

    if (claimsNew && 'tracking' in claimsNew) {
      data = claimsNew.tracking as unknown as any[]
    }

    if (data.length > 0) {
      return Array.from(
        new Set(
          data.map((tracking: any) => tracking.payer || tracking.manual_payer),
        ),
      )
    }

    return []
  }

  const confirmDeleteAction = useCallback(async (): Promise<void> => {
    const claimType = activeTab === 'claims' ? 'Claim' : 'Pre-Authorization'
    showLoading({ container: '.claim-delete-modal' })
    try {
      await deleteClaimById(claimIdToDelete)
      refreshClaims()
      setTimeout(() => {
        hideLoading('.claim-delete-modal')
        showToast(
          createSuccessToast(
            `${claimType} deleted successfully`,
            `${claimType} has been deleted successfully.`,
          ),
        )
      }, 1000)
    } catch (error) {
      logger.error('Error deleting claim:', error)
      setTimeout(() => {
        hideLoading('.claim-delete-modal')
        showToast(
          createFailedToast(
            `${claimType} not deleted`,
            `${claimType} not deleted. Please try again.`,
          ),
        )
      }, 1000)
    } finally {
      setShowConfirmDeleteModal(false)
    }
  }, [
    showLoading,
    hideLoading,
    showToast,
    claimIdToDelete,
    refreshClaims,
    activeTab,
  ])

  const handleProceedPost = async (): Promise<void> => {
    setShowPostConfirmationModal(false)
    const response = await postAllTransactions()
    if (response.statusCode === 200) {
      showToast(
        createSuccessToast(
          'Transactions posted successfully',
          'Transactions have been posted successfully.',
        ),
      )
      refreshClaims()
    } else {
      showToast(
        createFailedToast(
          'Transactions posting failed',
          'Transactions posting failed. Please try again later.',
        ),
      )
    }
  }

  const renderClaimsListEmpty = (
    kind: 'claims' | 'pre-auth',
  ): React.ReactElement => {
    const noun = kind === 'claims' ? 'claims' : 'pre-auths'
    const actionLabel = canAddClaim
      ? kind === 'claims'
        ? 'Add claim'
        : 'Add pre-auth'
      : 'Select patient'
    return (
      <EmptyState
        className="claims-empty"
        iconClass="mgc mgc_receive_money_fill"
        title={`No ${noun} found`}
        description={
          hasNoClaimRecords
            ? canAddClaim
              ? `You don't have any ${noun} on record yet.`
              : `Please select a patient to view their ${noun}.`
            : `No ${noun} match your search.`
        }
        actionLabel={actionLabel}
        onAction={
          canAddClaim
            ? openAddClaimModal
            : () => dispatch(requestPatientSelection())
        }
      />
    )
  }

  const renderTabContent = (): React.ReactElement => {
    if (isTabSkeleton) {
      return (
        <Row className="claims-page-row">
          <Col>
            <ClaimsSkeleton
              hideHeader
              variant={activeTab === 'tracking' ? 'tracking' : 'claims'}
            />
          </Col>
        </Row>
      )
    }

    if (activeTab === 'tracking') {
      return (
        <Row className="claims-page-row">
          <Col>
            <div className="claim-feature-grid claim-tracking-grid">
              {showTrackingEmpty ? (
                <EmptyState
                  className="claims-empty"
                  iconClass="mgc mgc_receive_money_fill"
                  title="No transactions found"
                  description="You don't have any transactions on record yet."
                  actionLabel="Add deposit"
                  onAction={openAddTrackingModal}
                />
              ) : (
                <TrackingManagement
                  trackingTransactions={currentTrackingData}
                  filters={filters}
                  searchQuery={searchQuery}
                  onPostSuccess={refreshClaims}
                  onDeleteSuccess={refreshClaims}
                  onAddDeposit={openAddTrackingModal}
                />
              )}
            </div>
          </Col>
        </Row>
      )
    }

    const kind = activeTab === 'pre-auth' ? 'pre-auth' : 'claims'
    return (
      <Row className="claims-page-row claims-page-row-no-scroll">
        <Col>
          <div className="claim-feature-grid claim-feature-grid-no-scroll">
            {showClaimsListEmpty ? (
              renderClaimsListEmpty(kind)
            ) : (
              <ClaimFeature
                onEditClaim={handleEditClaim}
                claims={currentClaimsData}
                filters={filters}
                activeTab={activeTab}
                searchQuery={searchQuery}
                onDeleteClaim={(idToDelete: string) => {
                  setClaimIdToDelete(idToDelete)
                  setShowConfirmDeleteModal(true)
                }}
                initialViewClaimId={initialViewClaimId}
                onInitialViewConsumed={handleInitialViewConsumed}
                emptyActionLabel={
                  canAddClaim
                    ? kind === 'claims'
                      ? 'Add claim'
                      : 'Add pre-auth'
                    : 'Select patient'
                }
                onEmptyAction={
                  canAddClaim
                    ? openAddClaimModal
                    : () => dispatch(requestPatientSelection())
                }
              />
            )}
          </div>
        </Col>
      </Row>
    )
  }

  return (
    <>
      <Container
        fluid
        className={`claims-page padd-812${isPageReady ? ' is-ready' : ''}`}
      >
        {pagePreviousState && (
          <GoBackButton previousPageState={pagePreviousState} />
        )}
        <Row
          className="align-items-center justify-content-between mb-2"
          style={{
            borderBottom: '1px solid var(--blue-200)',
            paddingBottom: '8px',
          }}
        >
          <Col md="auto" className="d-flex align-items-center gap-2">
            <ButtonGroup
              buttons={tabButtons}
              activeTab={activeTab}
              onButtonClick={handleChangeTab}
              className="claim-tabs-group"
            />
          </Col>
          {(activeTab === 'claims' || activeTab === 'pre-auth') && (
            <Col
              md="auto"
              className="d-flex align-items-center gap-2 claims-header-actions-col"
            >
              <StableInput
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                clearAllFilters={clearAllFilters}
                filter={{
                  data: {
                    status: [
                      'Approved',
                      'Partially Approved',
                      'Under Review',
                      'Rejected',
                    ],
                    insuranceCarrier: getUniqueInsuranceCarriers(),
                  },
                  labels: { status: 'Claim status', insuranceCarrier: 'Payer' },
                }}
                handleFilterChange={handleFilterChange}
                placeholder={`Search ${activeTab === 'claims' ? 'claims' : 'pre-auths'}...`}
              />

              <Button
                size="md"
                className={`button-primary ${
                  activeTab === 'pre-auth'
                    ? 'add-pre-auth-button'
                    : 'add-claim-button'
                }`}
                onClick={openAddClaimModal}
                disabled={!canAddClaim || showClaimEmptyCta}
              >
                {activeTab === 'pre-auth' ? 'Add pre-auth' : 'Add claim'}
              </Button>

              {/* Temporarily excluded — can be reused later if Settings are needed. */}
              {/* <Button
                size="md"
                className="button-secondary btn-procedure-settings"
                onClick={() => setIsProcedureSettingsModalOpen(true)}
              >
                <i className="mgc mgc_settings_3_fill"></i>
              </Button> */}

              <Button
                size="md"
                className="button-secondary btn-procedure-settings"
                onClick={() => setShowClaimsSimulationEditor(true)}
              >
                <i className="mgc mgc_edit_4_line"></i>
              </Button>
            </Col>
          )}

          {activeTab === 'tracking' && (
            <Col md="auto" className="d-flex align-items-center gap-2">
              <StableInput
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                clearAllFilters={clearAllFilters}
                filter={{
                  data: {
                    modeOfPayment: ['CHECK', 'ACH', 'VIRTUAL_CARD', 'WIRE'],
                    insuranceCarrier: getUniqueInsuranceCarriersTracking(),
                    checkStatus: [
                      'Pending',
                      'Processing',
                      'Deposited',
                      'Posted',
                    ],
                  },
                  labels: {
                    modeOfPayment: 'Mode of payment',
                    insuranceCarrier: 'Payer',
                    checkStatus: 'Status',
                  },
                }}
                handleFilterChange={handleFilterChange}
                placeholder={`Search transactions...`}
              />
              <Button
                size="md"
                className="button-primary add-deposit-button"
                onClick={openAddTrackingModal}
                disabled={showTrackingEmpty}
              >
                Add deposit
              </Button>
              <Button
                size="md"
                className="button-secondary"
                onClick={() => setShowPostConfirmationModal(true)}
                disabled={currentTrackingData.length === 0}
              >
                Post
              </Button>
            </Col>
          )}
        </Row>

        {renderTabContent()}
      </Container>

      {/* Add Claims Modal */}
      {isAddClaimsModal && (
        <>
          <AddClaimsModal
            isVisible={isAddClaimsModal}
            closeModal={() => {
              setIsAddClaimsModal(false)
            }}
            editingClaim={selectedClaim}
            activeTab={activeTab}
            refreshClaims={refreshClaims}
          />
        </>
      )}

      <DeleteConfirmationModal
        show={showConfirmDeleteModal}
        onHide={() => setShowConfirmDeleteModal(false)}
        onConfirm={confirmDeleteAction}
        title={
          activeTab === 'claims' ? 'Delete claim?' : 'Delete pre-authorization?'
        }
        bodyText={`Are you sure you want to delete ${activeTab === 'claims' ? 'claim' : 'pre-authorization'} ${
          currentClaimsData.find((c) => c.id === claimIdToDelete)
            ?.providerClaimId || ''
        }? This action can't be undone.`}
      />

      {/* Insurance Payment Modal */}
      {isPaymentModalOpen && selectedClaim && (
        <InsurancePaymentModal
          isVisible={isPaymentModalOpen}
          closeModal={() => setIsPaymentModalOpen(false)}
          selectedClaim={selectedClaim}
        />
      )}

      {/* Procedure Settings Modal */}
      {isProcedureSettingsModalOpen && (
        <ProcedureSettingsModal
          isVisible={isProcedureSettingsModalOpen}
          closeModal={() => setIsProcedureSettingsModalOpen(false)}
        />
      )}
      <AddClaimTracking
        isVisible={isAddTrackingModalVisible}
        closeModal={(isSuccess: boolean) => {
          setIsAddTrackingModalVisible(false)
          if (isSuccess) {
            refreshClaims()
          }
        }}
      />
      {showClaimsSimulationEditor && (
        <ClaimsSimulationEditor
          show={showClaimsSimulationEditor}
          onHide={() => setShowClaimsSimulationEditor(false)}
          data={currentClaimsData || []}
          onUpdate={() => {
            refreshClaims()
            setShowClaimsSimulationEditor(false)
          }}
        />
      )}

      <ConfirmationModal
        show={showPostConfirmationModal}
        onHide={() => setShowPostConfirmationModal(false)}
        title="Confirmation"
        className="confirmation-modal--alert"
        headerClassName="confirmation-modal__header"
        size="md"
        body={
          <div className="confirmation-modal__body">
            <p className="confirmation-modal__title">Mark as posted?</p>
            <p className="confirmation-modal__message">
              {
                'Are you sure you want to mark all "Deposited" transactions as posted?'
              }
            </p>
          </div>
        }
        footer={
          <div className="confirmation-modal__footer">
            <Button
              size="md"
              className="button-secondary"
              onClick={() => setShowPostConfirmationModal(false)}
            >
              Cancel
            </Button>
            <Button
              size="md"
              className="button-primary"
              onClick={handleProceedPost}
            >
              Proceed
            </Button>
          </div>
        }
      />
    </>
  )
}

export default Claims
